import { createContext, useContext, useMemo } from 'react';

import type { Mode, Notice } from './session';
import { deviceStorage, type KeyValueStorage } from './storage';

/**
 * What the app sees of the account layer.
 *
 * Outside `AccountDataProvider` (tests, anything mounted elsewhere) every hook
 * behaves exactly as it did before accounts: device mode, `localStorage`.
 */

export interface AccountData {
  /**
   *   device     logged out, or the sign-up questions aren't done: localStorage, as always
   *   importing  moving the device's plans into the account; the device's are shown
   *   account    the account's plans are shown and every change goes to it
   *   error      signed in, and the account couldn't be read
   */
  mode: Mode;
  /** A message for the toast, once. */
  notice: Notice | null;
  dismissNotice: () => void;
  retry: () => void;
  /** Sends every queued change; true once the server holds them all. */
  flush: () => Promise<boolean>;
}

export const AccountDataContext = createContext<AccountData>({
  mode: 'device',
  notice: null,
  dismissNotice: () => {},
  retry: () => {},
  flush: async () => true,
});

export function useAccountData(): AccountData {
  return useContext(AccountDataContext);
}

/**
 * Where the seven hooks keep their values. A new `epoch` means the values moved
 * (into the account, or to a newer copy of it) and every hook reads again.
 */
export interface StorageSource {
  epoch: number;
  create: () => KeyValueStorage;
}

export const StorageSourceContext = createContext<StorageSource>({
  epoch: 0,
  create: () => ({ ...deviceStorage }),
});

/**
 * The storage one of the seven hooks reads and writes through, in place of
 * `localStorage`. Its identity changes exactly when the hook must read its
 * value again; each hook instance gets its own.
 */
export function useAccountStorage(): KeyValueStorage {
  const source = useContext(StorageSourceContext);
  return useMemo(() => source.create(), [source]);
}
